import { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import CloseIcon from "@mui/icons-material/Close";

interface PhotoUploadProps {
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  label?: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  rotation: number
): Promise<string> {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve) => {
    image.onload = resolve;
  });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  const outputWidth = 300;
  const outputHeight = 400;
  canvas.width = outputWidth;
  canvas.height = outputHeight;

  ctx.save();

  if (rotation !== 0) {
    const tempCanvas = document.createElement("canvas");
    const tempCtx = tempCanvas.getContext("2d")!;
    const radians = (rotation * Math.PI) / 180;

    const sin = Math.abs(Math.sin(radians));
    const cos = Math.abs(Math.cos(radians));
    tempCanvas.width = image.width * cos + image.height * sin;
    tempCanvas.height = image.width * sin + image.height * cos;

    tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
    tempCtx.rotate(radians);
    tempCtx.drawImage(image, -image.width / 2, -image.height / 2);

    ctx.drawImage(
      tempCanvas,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      outputWidth,
      outputHeight
    );
  } else {
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      outputWidth,
      outputHeight
    );
  }

  ctx.restore();

  return canvas.toDataURL("image/jpeg", 0.85);
}

const PhotoUpload = ({ value, onChange, required, label }: PhotoUploadProps) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      alert("Imagem muito grande. Tamanho permitido: 10MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setDialogOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  async function handleCropConfirm() {
    if (!imageSrc || !croppedAreaPixels) return;
    const croppedBase64 = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
    onChange(croppedBase64);
    setDialogOpen(false);
    setImageSrc(null);
  }

  return (
    <Box>
      {label && (
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5, color: "#33303E" }}>
          {label}{required ? " *" : ""}
        </Typography>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {value ? (
        <Box sx={{ display: "inline-block", position: "relative" }}>
          <Box
            component="img"
            src={value}
            alt="Foto 3x4"
            sx={{
              width: 96,
              height: 128,
              borderRadius: "6px",
              border: "1px solid #e2e8f0",
              objectFit: "cover",
            }}
          />
          <IconButton
            size="small"
            onClick={() => onChange("")}
            sx={{
              position: "absolute",
              top: -8,
              right: -8,
              bgcolor: "#dc2626",
              color: "#fff",
              width: 20,
              height: 20,
              "&:hover": { bgcolor: "#b91c1c" },
            }}
          >
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
          <Box sx={{ mt: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              sx={{ textTransform: "none", fontSize: 12 }}
            >
              Trocar foto
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          onClick={() => fileInputRef.current?.click()}
          sx={{
            width: 96,
            height: 128,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            borderRadius: "6px",
            border: "2px dashed #cbd5e1",
            color: "#94a3b8",
            cursor: "pointer",
            transition: "all 0.15s",
            "&:hover": { borderColor: "#64748b", color: "#475569" },
          }}
        >
          <CameraAltIcon sx={{ fontSize: 24 }} />
          <Typography sx={{ fontSize: 11 }}>
            {required ? "Selecionar" : "Adicionar"}
          </Typography>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Recortar Foto 3x4</DialogTitle>
        <DialogContent>
          <Box sx={{ position: "relative", width: "100%", height: 300 }}>
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={3 / 4}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            )}
          </Box>
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ fontSize: 13, color: "#6b7490", minWidth: 40 }}>Zoom</Typography>
            <Slider
              value={zoom}
              onChange={(_, v) => setZoom(v as number)}
              min={1}
              max={3}
              step={0.1}
              sx={{ flex: 1 }}
            />
          </Box>
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RotateRightIcon />}
              onClick={() => setRotation((r) => (r + 90) % 360)}
              sx={{ textTransform: "none" }}
            >
              Girar
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleCropConfirm} sx={{ textTransform: "none" }}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhotoUpload;
